import { Box, Group, Text, Tooltip } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

const STEPS = [
  { key: "draft", label: "Draft", hint: "Company profile saved; verification not started or not yet submitted." },
  {
    key: "system",
    label: "System verified",
    hint: "Automated checks and review (when applicable). This step is active while your application is being processed.",
  },
  { key: "verified", label: "Verified", hint: "Profile verification is complete." },
] as const;

type NodeVariant = "completed" | "active" | "future" | "actionRequired";

function getState(status: string): { currentStep: number; actionRequired: boolean } {
  switch (status) {
    case "ACTION_REQUIRED":
      return { currentStep: 0, actionRequired: true };
    case "DRAFT":
      return { currentStep: 0, actionRequired: false };
    case "UNDER_REVIEW":
      return { currentStep: 1, actionRequired: false };
    case "SYSTEM_VERIFIED":
      return { currentStep: 2, actionRequired: false };
    case "VERIFIED":
      return { currentStep: STEPS.length, actionRequired: false };
    default:
      return { currentStep: 0, actionRequired: false };
  }
}

function getNodeVariant(index: number, currentStep: number, actionRequired: boolean): NodeVariant {
  if (actionRequired && index === 0) return "actionRequired";
  if (index < currentStep) return "completed";
  if (index === currentStep) return "active";
  return "future";
}

const NODE_STYLES: Record<NodeVariant, { bg: string; border: string; text: string; shadow?: string }> = {
  completed: { bg: "var(--mantine-color-green-6)", border: "var(--mantine-color-green-6)", text: "#fff" },
  active: {
    bg: "var(--mantine-color-primary-6)",
    border: "var(--mantine-color-primary-6)",
    text: "#fff",
    shadow: "0 0 0 3px var(--mantine-color-primary-2)",
  },
  future: { bg: "#fff", border: "var(--mantine-color-gray-4)", text: "var(--mantine-color-gray-6)" },
  actionRequired: {
    bg: "var(--mantine-color-orange-6)",
    border: "var(--mantine-color-orange-6)",
    text: "#fff",
    shadow: "0 0 0 3px var(--mantine-color-orange-2)",
  },
};

const LABEL_COLORS: Record<NodeVariant, string> = {
  completed: "var(--mantine-color-green-8)",
  active: "var(--mantine-color-primary-8)",
  future: "var(--mantine-color-gray-6)",
  actionRequired: "var(--mantine-color-orange-8)",
};

function connectorColor(index: number, currentStep: number, actionRequired: boolean): string {
  if (actionRequired) return "var(--mantine-color-gray-3)";
  if (index < currentStep) return "var(--mantine-color-green-6)";
  return "var(--mantine-color-gray-3)";
}

interface ProfileVerificationStepperProps {
  status: string;
  /** Fits in profile header; still readable */
  compact?: boolean;
}

export default function ProfileVerificationStepper({ status, compact = false }: ProfileVerificationStepperProps) {
  const { currentStep, actionRequired } = getState(status);

  const circle = compact ? 26 : 30;
  const borderW = compact ? 2.5 : 2.5;
  const checkSize = compact ? 14 : 15;
  const numFz = compact ? 12 : 13;
  const labelFz = compact ? 11 : 10;
  const labelMt = compact ? 6 : 6;
  const labelMaw = compact ? 92 : 88;
  const titleMb = compact ? 6 : "sm";
  const titleFz = compact ? 12 : "xs";
  const connectorH = compact ? 3 : 2.5;
  const connectorMinW = compact ? 6 : 8;
  const connectorFixedW = compact ? 20 : undefined;
  const connectorMx = compact ? 3 : 4;
  const connectorMb = compact ? 22 : 22;
  const noteFz = compact ? 10 : 9;

  return (
    <Box w={compact ? "auto" : "100%"} py={compact ? 0 : "xs"} px={compact ? 0 : { base: "xs", sm: "sm" }} style={{ flexShrink: 0 }}>
      <Text
        fz={titleFz}
        fw={700}
        c="gray.7"
        mb={titleMb}
        tt="uppercase"
        style={{ letterSpacing: compact ? "0.04em" : "0.06em" }}
        ta={compact ? "right" : "left"}
      >
        Verification
      </Text>
      <Group
        align="flex-start"
        gap={0}
        w={compact ? "auto" : "100%"}
        wrap="nowrap"
        justify={compact ? "flex-end" : "flex-start"}
        style={{ overflowX: compact ? "visible" : "auto", paddingBottom: compact ? 2 : 4 }}
      >
        {STEPS.map((step, i) => {
          const variant = getNodeVariant(i, currentStep, actionRequired);
          const style = NODE_STYLES[variant];

          const node = (
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                flexShrink: 0,
                maxWidth: labelMaw,
              }}
            >
              <Box
                w={circle}
                h={circle}
                style={{
                  borderRadius: "50%",
                  backgroundColor: style.bg,
                  border: `${borderW}px solid ${style.border}`,
                  boxShadow: style.shadow || "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
              >
                {variant === "completed" ? (
                  <IconCheck size={checkSize} color="#fff" stroke={2.5} />
                ) : (
                  <Text fz={numFz} fw={700} c={style.text} lh={1}>
                    {i + 1}
                  </Text>
                )}
              </Box>

              <Text
                fz={labelFz}
                fw={600}
                mt={labelMt}
                c={LABEL_COLORS[variant]}
                ta="center"
                maw={labelMaw}
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  lineHeight: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {step.label}
              </Text>

              {variant === "actionRequired" && (
                <Text fz={noteFz} fw={600} mt={4} c="orange.7" ta="center" maw={labelMaw} lh={1.25}>
                  Updates needed
                </Text>
              )}
            </Box>
          );

          return (
            <Box
              key={step.key}
              style={{
                display: "flex",
                alignItems: "center",
                flex: compact || i === STEPS.length - 1 ? "none" : 1,
                minWidth: 0,
              }}
            >
              <Tooltip label={step.hint} position="bottom" multiline w={240} withArrow openDelay={400}>
                <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "help" }}>
                  {node}
                </Box>
              </Tooltip>

              {i < STEPS.length - 1 && (
                <Box
                  style={{
                    flex: compact ? "none" : 1,
                    width: connectorFixedW,
                    height: connectorH,
                    minWidth: connectorMinW,
                    borderRadius: 2,
                    backgroundColor: connectorColor(i, currentStep, actionRequired),
                    margin: `0 ${connectorMx}px`,
                    marginBottom: connectorMb,
                    alignSelf: "center",
                    transition: "background-color 0.3s ease",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Group>
    </Box>
  );
}
